import type { FC } from 'react';
import { type Control, Controller } from 'react-hook-form';
import Button from '@components/Button';
import Form from '@components/Form';
import InputSelect from '@components/InputSelect';
import InputText from '@components/InputText';
import InputTextarea from '@components/InputTextarea';
import Modal from '@components/Modal';
import { preventDefaultEnterSubmit } from '@helpers/preventDefaultEnterSubmit';
import { EVAL_CONFIG_STARTERS } from '@root/helpers/constants';

import { EVAL_MODEL_ID_OPTIONS, WORKFLOW_TYPE_OPTIONS } from '../constants';
import type { CreateEvalConfigFormState } from '../types';

const STARTER_OPTIONS = EVAL_CONFIG_STARTERS.map(({ id, name }) => ({ value: id, name }));

interface CreateEvalConfigModalProps {
  control: Control<CreateEvalConfigFormState>;
  isLoading: boolean;
  isValid: boolean;
  /** A starter brings its own packs and workflow type, so that field is set by the server. */
  isStarterChosen: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

export const CreateEvalConfigModal: FC<CreateEvalConfigModalProps> = ({
  control,
  isLoading,
  isValid,
  isStarterChosen,
  onClose,
  onSubmit,
}) => (
  <Modal
    className="w-lg gap-0"
    onClose={onClose}
    open
    shouldShowCloseIcon={false}
    title="Create eval config"
    titleClassName="text-lg leading-6 font-medium justify-center!"
  >
    <div className="flex flex-col gap-4 px-6 pt-2 pb-4">
      <p className="text-sm leading-5 font-normal text-gray-500">
        Configure a new Contract Review Bot evaluation. Start from the current champion prompts, or blank and add prompt
        and guideline packs after creating the config.
      </p>
      <Form
        className="flex w-full flex-col gap-4"
        onSubmit={onSubmit}
        aria-label="Create eval config form"
        onKeyDown={preventDefaultEnterSubmit}
      >
        <Controller
          control={control}
          name="name"
          render={({ field, fieldState: { error } }) => (
            <InputText
              {...field}
              labelText="Name"
              isRequired
              errors={error?.message}
              dataTestId="text:eval-config-name"
            />
          )}
        />
        <Controller
          control={control}
          name="copy_from"
          render={({ field, fieldState: { error } }) => (
            <InputSelect
              {...field}
              isRequired
              isSearchable={false}
              labelText="Starting point"
              options={STARTER_OPTIONS}
              errors={error?.message}
              dataTestId="select:eval-config-copy-from"
            />
          )}
        />
        {/* The starter's packs were written for one workflow; the server applies it. */}
        <Controller
          control={control}
          name="workflow_type"
          render={({ field, fieldState: { error } }) => (
            <InputSelect
              {...field}
              isRequired
              isSearchable={false}
              isDisabled={isStarterChosen}
              labelText={isStarterChosen ? 'Workflow type (set by the starting point)' : 'Workflow type'}
              options={WORKFLOW_TYPE_OPTIONS}
              errors={error?.message}
              dataTestId="select:eval-config-workflow-type"
            />
          )}
        />
        <Controller
          control={control}
          name="model_id"
          render={({ field, fieldState: { error } }) => (
            <InputSelect
              {...field}
              isRequired
              isSearchable={false}
              labelText="Model"
              options={EVAL_MODEL_ID_OPTIONS}
              errors={error?.message}
              dataTestId="select:eval-config-model-id"
            />
          )}
        />
        <Controller
          control={control}
          name="description"
          render={({ field, fieldState: { error } }) => (
            <InputTextarea
              {...field}
              labelText="Description"
              errors={error?.message}
              dataTestId="textarea:eval-config-description"
            />
          )}
        />
        <div className="mt-2 flex flex-row-reverse gap-3">
          <Button type="submit" kind="primary" isDisabled={!isValid || isLoading} loading={isLoading} className="h-9.5">
            Create
          </Button>
          <Button type="button" onClick={onClose} isDisabled={isLoading} className="h-9.5">
            Cancel
          </Button>
        </div>
      </Form>
    </div>
  </Modal>
);
